import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  X,
  Loader2,
  Calendar,
  Cpu,
  BatteryMedium,
  ExternalLink,
} from "lucide-react";
import Modal from "components/common/Modal";
import betaAPI from "v2/api";
import WearableStatusBadge from "./WearableStatusBadge";
import { formatDate, formatMac, fieldLabelDisplay } from "./constants";
import { dateLocale } from "i18n";

// labelKey is translated at render time.
const TYPE_META = {
  match: {
    labelKey: "admin.manufacturing.sessionTypes.match",
    color: "#E07A3A",
    bg: "#FEF3E7",
  },
  training: {
    labelKey: "admin.manufacturing.sessionTypes.training",
    color: "#6B7280",
    bg: "#F3F4F6",
  },
};

const SESSION_STATUS_META = {
  scheduled: {
    labelKey: "admin.manufacturing.sessionStatuses.scheduled",
    color: "#6B7280",
    bg: "#F3F4F6",
  },
  active: {
    labelKey: "common.status.active",
    color: "#16A34A",
    bg: "#DCFCE7",
  },
  completed: {
    labelKey: "admin.manufacturing.sessionStatuses.completed",
    color: "#2563EB",
    bg: "#EFF6FF",
  },
  cancelled: {
    labelKey: "admin.manufacturing.sessionStatuses.cancelled",
    color: "#DC2626",
    bg: "#FEF2F2",
  },
};

const startTimeDisplay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString(dateLocale("en-US"), {
    hour: "numeric",
    minute: "2-digit",
  });
};

const HeaderStat = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-[#9CA3AF]">{label}</p>
    <p className="mt-0.5 text-sm font-semibold text-[#111827]">{value}</p>
  </div>
);

const AssignmentRow = ({ assignment, isCurrent, onOpenWearable }) => {
  const { t } = useTranslation();
  const label = fieldLabelDisplay(assignment);
  const clickable = Boolean(assignment.ble_mac) && Boolean(onOpenWearable);
  const macDisplay = assignment.wearable_mac
    ? formatMac(assignment.wearable_mac)
    : null;

  return (
    <tr
      onClick={clickable ? () => onOpenWearable(assignment.ble_mac) : undefined}
      className={`border-b border-[#F3F4F6] last:border-b-0 ${
        clickable
          ? "cursor-pointer transition duration-150 hover:bg-[#FAFAFA]"
          : ""
      } ${isCurrent ? "bg-[#FEF3E7]/50" : ""}`}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
            <Cpu size={13} className="text-[#6B7280]" />
          </div>
          <div className="min-w-0">
            <p className="whitespace-nowrap text-sm font-medium text-[#111827]">
              {label ||
                assignment.serial ||
                macDisplay ||
                t("admin.manufacturing.sessionWearables.noUnit")}
              {isCurrent && (
                <span className="ml-2 rounded bg-[#FEF3E7] px-1.5 py-0.5 text-[10px] font-medium text-[#E07A3A]">
                  {t("admin.manufacturing.sessionWearables.thisUnit")}
                </span>
              )}
            </p>
            <p className="mt-0.5 whitespace-nowrap font-mono text-[11px] text-[#9CA3AF]">
              {label && assignment.serial ? `${assignment.serial} · ` : ""}
              {macDisplay || "—"}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-[#374151]">
        {assignment.player_name}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-[#374151]">
        {assignment.impacts}
      </td>
      <td className="px-4 py-3 text-right text-sm tabular-nums text-[#374151]">
        {assignment.load != null ? assignment.load : "—"}
      </td>
      <td className="px-4 py-3">
        {assignment.latest_battery_pct != null ? (
          <span className="flex items-center justify-end gap-1 text-sm tabular-nums text-[#374151]">
            <BatteryMedium size={14} className="text-[#9CA3AF]" />
            {assignment.latest_battery_pct}%
          </span>
        ) : (
          <span className="block text-right text-sm text-[#9CA3AF]">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {assignment.wearable_status ? (
          <WearableStatusBadge status={assignment.wearable_status} size="xs" />
        ) : (
          <span className="text-[11px] text-[#9CA3AF]">
            {macDisplay
              ? t("admin.manufacturing.sessionWearables.unknownUnit")
              : "—"}
          </span>
        )}
      </td>
    </tr>
  );
};

const TH = ({ children, right }) => (
  <th
    className={`sticky top-0 z-10 bg-white px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF] ${
      right ? "text-right" : "text-left"
    }`}
  >
    {children}
  </th>
);

/**
 * Everything an admin needs about one session, wearable-first: header context
 * (who/where/when), totals, and every unit assigned with its athlete and
 * rollups. Cross-workspace, admin only.
 *
 * - `linkToUnits={false}` disables unit row navigation where leaving the page
 *   would lose context (e.g. inside the connected-device workbench).
 * - "Open session" always navigates to the session in the dashboard;
 *   `onNavigateAway` lets the host tear down whatever is above it (the
 *   workbench closes itself so the destination is actually visible).
 */
const SessionWearablesModal = ({
  show,
  onClose,
  sessionId,
  currentBleMac,
  linkToUnits = true,
  onNavigateAway,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!show || !sessionId) return;
    let active = true;
    setLoading(true);
    setError(false);
    setSession(null);
    setAssignments([]);
    betaAPI
      .getSessionWearables({ sessionId })
      .then((res) => {
        if (!active) return;
        setSession(res.data?.session || null);
        setAssignments(res.data?.assignments?.results || []);
      })
      .catch((err) => {
        console.error("Failed to load session wearables", err);
        if (active) setError(true);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [show, sessionId]);

  const handleOpenWearable = (bleMac) => {
    onClose();
    onNavigateAway?.();
    navigate(`/v2/admin/manufacturing/wearables/${bleMac}`);
  };

  const handleOpenSession = () => {
    if (!session) return;
    onClose();
    onNavigateAway?.();
    navigate(
      `/v2/${session.workspace_id}/team/${session.team_id}/session/${session.id}`
    );
  };

  const currentMacBare = (currentBleMac || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();
  const typeMeta = TYPE_META[session?.type] || TYPE_META.training;
  const statusMeta = SESSION_STATUS_META[session?.status];
  const startTime = startTimeDisplay(session?.start_time);
  const unitCount = assignments.filter((a) => a.wearable_mac).length;

  return (
    <Modal show={show} onClose={onClose} maxWidth="840px">
      {/* Header — fixed */}
      <div className="flex items-start justify-between gap-4 border-b border-[#F3F4F6] px-6 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#FEF3E7]">
            <Calendar size={17} className="text-[#E07A3A]" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-[#111827]">
                {loading
                  ? t("admin.manufacturing.sessionWearables.loadingSession")
                  : session?.name ||
                    t("admin.manufacturing.sessionWearables.untitled")}
              </h2>
              {session && (
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                  style={{
                    color: typeMeta.color,
                    backgroundColor: typeMeta.bg,
                  }}
                >
                  {t(typeMeta.labelKey)}
                </span>
              )}
              {statusMeta && (
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                  style={{
                    color: statusMeta.color,
                    backgroundColor: statusMeta.bg,
                  }}
                >
                  {t(statusMeta.labelKey)}
                </span>
              )}
            </div>
            {session && (
              <p className="mt-0.5 truncate text-xs text-[#6B7280]">
                {startTime
                  ? t("admin.manufacturing.sessionWearables.contextWithTime", {
                      workspace: session.workspace_name,
                      team: session.team_name,
                      date: formatDate(session.date),
                      time: startTime,
                    })
                  : t("admin.manufacturing.sessionWearables.context", {
                      workspace: session.workspace_name,
                      team: session.team_name,
                      date: formatDate(session.date),
                    })}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {session && (
            <button
              onClick={handleOpenSession}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <ExternalLink size={13} />
              {t("admin.manufacturing.sessionWearables.openSession")}
            </button>
          )}
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B7280] transition duration-150 hover:bg-[#F3F4F6]"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={22} className="animate-spin text-[#E07A3A]" />
        </div>
      ) : error ? (
        <p className="py-16 text-center text-sm text-[#9CA3AF]">
          {t("admin.manufacturing.sessionWearables.loadFailed")}
        </p>
      ) : (
        <>
          {/* Totals — fixed */}
          {session && (
            <div className="grid flex-shrink-0 grid-cols-4 gap-4 border-b border-[#F3F4F6] px-6 py-3.5">
              <HeaderStat
                label={t("admin.manufacturing.sessionWearables.wearables")}
                value={unitCount}
              />
              <HeaderStat
                label={t("admin.manufacturing.sessionWearables.athletes")}
                value={assignments.length}
              />
              <HeaderStat
                label={t("admin.manufacturing.sessionWearables.totalImpacts")}
                value={session.total_impacts}
              />
              <HeaderStat
                label={t("admin.manufacturing.sessionWearables.peakG")}
                value={session.peak_g != null ? session.peak_g : "—"}
              />
            </div>
          )}

          {/* Roster — scrolls on its own; header row stays pinned */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {assignments.length === 0 ? (
              <p className="py-16 text-center text-sm text-[#9CA3AF]">
                {t("admin.manufacturing.sessionWearables.noAssignments")}
              </p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#F3F4F6]">
                    <TH>
                      {t("admin.manufacturing.sessionWearables.columns.unit")}
                    </TH>
                    <TH>
                      {t(
                        "admin.manufacturing.sessionWearables.columns.athlete"
                      )}
                    </TH>
                    <TH right>
                      {t(
                        "admin.manufacturing.sessionWearables.columns.impacts"
                      )}
                    </TH>
                    <TH right>
                      {t("admin.manufacturing.sessionWearables.columns.load")}
                    </TH>
                    <TH right>
                      {t(
                        "admin.manufacturing.sessionWearables.columns.battery"
                      )}
                    </TH>
                    <TH right>
                      {t("admin.manufacturing.sessionWearables.columns.status")}
                    </TH>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => (
                    <AssignmentRow
                      key={assignment.id}
                      assignment={assignment}
                      isCurrent={
                        Boolean(assignment.ble_mac) &&
                        assignment.ble_mac === currentMacBare
                      }
                      onOpenWearable={linkToUnits ? handleOpenWearable : null}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default SessionWearablesModal;
